import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { SurfaceColorField, SURFACE_COLOR_FIELD_AI_SCHEMA } from '../fields/widgets/surface-color-field';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  resolveBackgroundColorWithOpacity,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import { createSpacingFields } from '../fields/spacing-fields';
import { CmsRichTextMenu, CmsTextRichTextMenu, cmsRichTextSelector } from '../fields/widgets/richtext-menu';
import { interpolateHtml } from '../interpolate-html';
import { getCmsVariables } from '../interpolation';
import { coerceTextHeadingsToParagraphs, sanitizeCmsHtml } from '../sanitize-html';
import {
  BLOCK_WIDTHS,
  BODY_LINE_HEIGHT,
  BODY_LINE_HEIGHTS,
  BODY_TEXT_SIZES,
  FONT_ROLES,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type {
  BlockWidthOption,
  BodyLineHeight,
  BodyTextSize,
  BoxSpacing,
  FontRole,
  MaxWidthOption,
} from '../fields/tokens';

export const DEFAULT_PARAGRAPH_HTML = '<p>Start writing here...</p>';

type ParagraphTextSize = BodyTextSize | 'system';

export type ParagraphProps = {
  text: string;
  size: ParagraphTextSize;
  lineHeight: BodyLineHeight;
  font: FontRole;
  color: string;
  /** Either a surface role key (e.g. `'surface'`) or a custom hex (e.g. `'#ff0000'`). */
  background: string;
  backgroundOpacity: number;
  width: BlockWidthOption;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/** See heading.tsx for the `--cms-*` over `--dcm-*` precedence this relies on. */
const StyledParagraph = styled.div`
  margin: 0;
  background: var(--cms-paragraph-background, ${SURFACE_COLOR_ROLES.none});
  color: var(--cms-paragraph-color, ${TEXT_COLOR_ROLES.text});
  font-family: var(--cms-paragraph-font-family, ${FONT_ROLES.body});
  font-size: var(--cms-paragraph-font-size, ${BODY_TEXT_SIZES.lg});
  line-height: var(--cms-paragraph-line-height, ${BODY_LINE_HEIGHT});
  width: var(--cms-paragraph-width, ${BLOCK_WIDTHS.fill});
  max-width: var(--cms-paragraph-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-paragraph-padding, 0px);

  > :first-child,
  .ProseMirror > :first-child {
    margin-top: 0;
  }

  > :last-child,
  .ProseMirror > :last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--dcm-cms-paragraph-link-color, var(--dcm-color-link, currentColor));
    text-decoration: underline;
  }
`;

const SPACING_FIELDS = createSpacingFields();

const SIZE_OPTIONS: { label: string; value: ParagraphTextSize }[] = [
  { label: 'System default', value: 'system' },
  { label: 'Extra small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra large', value: 'xl' },
  { label: '2X large', value: 'xxl' },
];

const LINE_HEIGHT_OPTIONS: { label: string; value: BodyLineHeight }[] = [
  { label: 'System default', value: 'system' },
  { label: 'Tight', value: 'tight' },
  { label: 'Comfortable', value: 'comfortable' },
  { label: 'Spacious', value: 'spacious' },
];

const LINE_WIDTH_OPTIONS: { label: string; value: MaxWidthOption }[] = [
  { label: 'Full available width', value: 'none' },
  { label: 'Normal', value: 'normal' },
  { label: 'Narrow', value: 'narrow' },
];

export const paragraphConfig: ComponentConfig<ParagraphProps> = {
  label: 'Text',
  ai: {
    instructions:
      'A rich-text block for descriptions, content paragraphs, and explanatory text. Supports formatting, lists, alignment, links, and CMS variable tokens.',
  },
  fields: {
    text: {
      type: 'richtext',
      label: 'Text content',
      contentEditable: true,
      renderMenu: CmsTextRichTextMenu,
      renderInlineMenu: CmsRichTextMenu,
      tiptap: { selector: cmsRichTextSelector },
      options: {
        heading: false,
        link: { openOnClick: false },
      },
    },
    size: {
      type: 'select',
      label: 'Text size',
      options: SIZE_OPTIONS,
      ai: {
        schema: { type: 'string', enum: SIZE_OPTIONS.map(({ value }) => value) },
        instructions: "Body text size from the storefront's shared scale. 'lg' suits most page copy.",
      },
    },
    lineHeight: {
      type: 'select',
      label: 'Line height',
      options: LINE_HEIGHT_OPTIONS,
      ai: {
        schema: { type: 'string', enum: LINE_HEIGHT_OPTIONS.map(({ value }) => value) },
        instructions:
          "Line height multiplier for the text. Leave as 'system' to inherit the theme default; choose tight, comfortable, or spacious when visual rhythm requires it.",
      },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: {
        schema: { type: 'string', enum: ['heading', 'body'] },
        instructions: "Which of the merchant's two configured fonts to use. Body copy should almost always use 'body'.",
      },
    },
    color: {
      type: 'custom',
      label: 'Text color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value such as #2c3236. Prefer a role so the text follows merchant branding.",
        },
      },
    },
    background: {
      type: 'custom',
      label: 'Background',
      render: SurfaceColorField,
      ai: {
        schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
        instructions:
          "Optional fill behind the text. Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit', value: 'fit' },
        { label: 'Fill', value: 'fill' },
      ],
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Line width',
      options: LINE_WIDTH_OPTIONS,
      ai: {
        schema: { type: 'string', enum: ['none', 'normal', 'narrow'] },
        instructions:
          'Constrains line length for readability. Prefer Full available width unless text wrap needs constraint.',
      },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  resolveFields: (data, { fields }) => resolveBackgroundOpacityFields(data.props.background, fields),
  defaultProps: {
    text: DEFAULT_PARAGRAPH_HTML,
    size: 'system',
    lineHeight: 'system',
    font: 'body',
    color: 'text',
    background: 'none',
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    width: 'fill',
    maxWidth: 'none',
    padding: NO_SPACING,
  },
  render: ({
    text = DEFAULT_PARAGRAPH_HTML,
    size = 'system',
    lineHeight = 'system',
    font = 'body',
    color = 'text',
    background = 'none',
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    width = 'fill',
    maxWidth = 'none',
    padding,
    puck,
  }) => {
    const content = text as unknown;
    const resolvedLineHeight = lineHeight === 'system' ? undefined : lookup(BODY_LINE_HEIGHTS, lineHeight, undefined);
    const style = {
      '--cms-paragraph-background': resolveBackgroundColorWithOpacity(background, backgroundOpacity),
      '--cms-paragraph-color': resolveTextColor(color),
      '--cms-paragraph-font-family': lookup(FONT_ROLES, font, FONT_ROLES.body),
      ...(size === 'system' ? {} : { '--cms-paragraph-font-size': lookup(BODY_TEXT_SIZES, size, BODY_TEXT_SIZES.lg) }),
      ...(resolvedLineHeight ? { '--cms-paragraph-line-height': resolvedLineHeight } : {}),
      '--cms-paragraph-width': lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS.fill),
      '--cms-paragraph-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
      '--cms-paragraph-padding': spacingToCss(padding),
    } as React.CSSProperties;

    // Inside <Puck>, the built-in richtext field transform replaces the saved HTML string with a
    // live Tiptap ReactNode before this render function runs. That node is the editor itself: it must
    // never pass through interpolation or sanitisation, which would stringify it and risk replacing
    // the literal {{tokens}} in the authoring buffer with sample values. Puck's production <Render>
    // would also wrap a `richtext` field; both renderer configs use createProductionComponents to
    // expose this one production field as `textarea`, making the raw saved HTML reach the branch below.
    if (typeof content !== 'string') {
      return <StyledParagraph style={style}>{content as React.ReactNode}</StyledParagraph>;
    }

    const interpolatedHtml = interpolateHtml(content, getCmsVariables(puck));
    const renderedHtml = sanitizeCmsHtml(coerceTextHeadingsToParagraphs(interpolatedHtml));
    return <StyledParagraph style={style} dangerouslySetInnerHTML={{ __html: renderedHtml }} />;
  },
};
